import { getLocale, plural, t } from "@/i18n/locale";
import { resolvePublisherAvatarUrl, type CapsuleSummary } from "@/api/storeClient";

/**
 * SPEC-O preview media (docs/rfcs/run-ux-adrs.md — FROZEN wire shape,
 * mirrored here as the view-model's camelCase equivalent). `kind` selects
 * which media field is populated; `"live"` is the exception path that keeps
 * using the existing curated live-iframe autoplay flow (`previewArtifactId`/
 * `previewPath`/`liveUrl` below), not the default for every item.
 */
export type ShowcasePreview = {
  kind: "video" | "screenshots" | "cover" | "live";
  videoUrl?: string | null;
  screenshotUrls?: string[] | null;
  coverUrl?: string | null;
  liveArtifactId?: string | null;
  livePath?: string | null;
};

/**
 * Discover/Showcase feed item — a view-model derived from a Store
 * {@link CapsuleSummary}. Ported from ato-web.
 */
export type RunFeedItem = {
  /**
   * `capsules.id` for a Store capsule; a hand-written constant for a curated
   * demo. Use `storeCapsuleId()` rather than reading this directly when the
   * value has to be a real DB id — analytics joins `capsules` on it, and a
   * curated demo has no row there.
   */
  id: string;
  slug: string;
  ref: string;
  /**
   * True only for an item built from a real `CapsuleSummary`. Curated demos
   * (featuredApps.ts) look identical otherwise but exist only in the frontend,
   * so `id` is not a DB id and capsule-scoped analytics must not claim it is.
   */
  isStoreCapsule?: boolean;
  /** Flat per-launch price in cents (billing v2 S5); 0/absent = free. */
  priceCents?: number;
  currency?: string;
  name: string;
  shortDescription: string;
  iconUrl: string;
  coverUrl: string;
  ownerHandle: string;
  ownerDisplay: string;
  ownerAvatarUrl: string;
  sourceUrl: string;
  /** Source repository (e.g. GitHub) the capsule was built from, when known. */
  repository?: string;
  postedAt: string | null;
  surfaces: string[];
  /** Store taxonomy top-level category id (src/store_taxonomy.ts, API side). */
  category?: string | null;
  /** Freeform publisher-chosen keywords (distinct from the taxonomy above). */
  tags?: string[];
  isFrontendOnly: boolean;
  liveUrl?: string;
  fullAppUrl?: string;
  /**
   * When true, the Showcase embeds `liveUrl` with `?autoplay=1` so the widget
   * plays a scripted, non-interactive demo of itself. The scenario lives in the
   * widget app (user-content origin), not here. Opt-in per featured item.
   */
  previewAutoplay?: boolean;
  /**
   * Immutable artifact identity for the preview, served from the isolated
   * user-content origin (`<artifact-id>.atousercontent.com`). When set, the feed
   * builds the iframe URL from this via {@link buildPreviewAssetUrl} instead of
   * the legacy name-based `liveUrl`. See docs/rfcs/accepted/showcase-url-model.md.
   */
  previewArtifactId?: string;
  /** Path within the preview artifact bundle, e.g. `"widget/index.html"`. */
  previewPath?: string;
  runs: number | null;
  vouches: number | null;

  // ── SPEC-O fields (Run-UX plan PR-M) — all optional/nullable so existing
  // callers (curated featuredApps, today's capsule-search mapping) keep
  // compiling untouched; only populated once a source actually carries them
  // (the real GET /v1/showcase/feed endpoint, PR-O1). ──
  /** Preview-media priority order (video > screenshots > cover/live). Absent
   *  for items from today's capsule search — those fall back to the legacy
   *  `coverUrl`/`previewArtifactId`/`liveUrl` fields above. */
  preview?: ShowcasePreview | null;
  launchKind?: "cloud" | "desktop" | "external" | "unavailable" | null;
  /** Plumbed through for a future guest-run PR — NOT enforced or rendered
   *  anywhere yet (SPEC-G guest design is still awaiting sign-off). */
  guestRunnable?: boolean | null;
  guestBlocker?: string | null;
  snapshotReady?: boolean | null;
  estimatedStartMs?: number | null;
  allowedDurationSec?: number | null;
  requiresLoginReason?: string | null;
  verificationState?: "green" | "stale" | "failed" | null;
  contentQualityState?: "ready" | "missing_preview" | "missing_icon" | "unverified" | null;
};

/** "just now" / "5m ago" / "3h ago" / "12d ago", falling back to a locale
 *  date past 30 days — shared by comment timestamps (CommentsTab) and the
 *  gallery card's posted-time metadata (RichAppCard). */
const SQLITE_UTC_TIMESTAMP = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

/**
 * D1 rows still contain both ISO timestamps and SQLite `datetime('now')`
 * timestamps. Browsers interpret the latter as local time unless the UTC
 * zone is made explicit, which makes Studio's correctly sorted rows display
 * misleading relative ages outside UTC.
 */
export function parseApiTimestamp(value: string): Date {
  const normalized = SQLITE_UTC_TIMESTAMP.test(value)
    ? `${value.replace(" ", "T")}Z`
    : value;
  return new Date(normalized);
}

export function formatRelativeTime(iso: string): string {
  const date = parseApiTimestamp(iso);
  const ms = Date.now() - date.getTime();
  const mins = Math.floor(ms / 60_000);
  if (getLocale() !== "en") {
    if (mins < 1) return t("just now");
    const relative = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
    if (mins < 60) return relative.format(-mins, "minute");
    if (mins < 24 * 60) return relative.format(-Math.floor(mins / 60), "hour");
    if (mins < 30 * 24 * 60) return relative.format(-Math.floor(mins / (24 * 60)), "day");
    return date.toLocaleDateString(getLocale());
  }
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString(getLocale());
}

/** "ai_agents" -> "Ai Agents" — a readable label without a taxonomy lookup
 *  round-trip (see GET /v1/store/taxonomy for the canonical label if this
 *  ever needs to be exact rather than good-enough). */
export function humanizeCategory(id: string): string {
  return id
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const WEB_SURFACES = new Set(["web_ui", "static_page"]);
const NATIVE_SURFACES = new Set(["desktop_ui", "cli", "background_service"]);

function pickIcon(s: CapsuleSummary): string {
  return (s.store_icon || s.icon || "").trim();
}

function pickCover(s: CapsuleSummary): string {
  const candidates = [
    s.store_cover_image,
    s.cover_image,
    Array.isArray(s.screenshots) ? s.screenshots[0] : undefined,
  ];
  for (const c of candidates) {
    const v = (c ?? "").trim();
    if (v) return v;
  }
  return "";
}

export function isFrontendOnly(surfaces: string[] | undefined | null): boolean {
  const list = Array.isArray(surfaces) ? surfaces : [];
  const hasWeb = list.some((sfc) => WEB_SURFACES.has(sfc));
  const hasNative = list.some((sfc) => NATIVE_SURFACES.has(sfc));
  return hasWeb && !hasNative;
}

export function capsuleRef(s: CapsuleSummary): string {
  const handle = (s.publisher?.handle ?? "").trim();
  const slug = (s.slug ?? "").trim();
  return handle ? `${handle}/${slug}` : slug;
}

function readRunsCount(s: CapsuleSummary): number | null {
  if (typeof s.run_count === "number" && Number.isFinite(s.run_count)) return s.run_count;
  if (typeof s.runCount === "number" && Number.isFinite(s.runCount)) return s.runCount;
  return null;
}

/** `vouches_count` (list/detail response) or its camelCase twin — see
 *  ato-api's capsules.ts, which returns the denormalized counter either way
 *  depending on route. */
export function readVouchesCount(s: CapsuleSummary): number | null {
  if (typeof s.vouches_count === "number" && Number.isFinite(s.vouches_count)) {
    return s.vouches_count;
  }
  return typeof s.vouchesCount === "number" && Number.isFinite(s.vouchesCount)
    ? s.vouchesCount
    : null;
}

/** `comments_count` (GET /v1/capsules list) or its camelCase twin. Absent on
 *  older routes/deploys — null means "don't render this stat", not zero. */
export function readCommentsCount(s: CapsuleSummary): number | null {
  if (typeof s.comments_count === "number" && Number.isFinite(s.comments_count)) {
    return s.comments_count;
  }
  return typeof s.commentsCount === "number" && Number.isFinite(s.commentsCount)
    ? s.commentsCount
    : null;
}

/** The DB capsule id an event may be attributed to, or null for a curated
 *  demo that has no `capsules` row. */
export function storeCapsuleId(item: {
  id?: string;
  isStoreCapsule?: boolean;
} | null | undefined): string | undefined {
  return item?.isStoreCapsule && item.id ? item.id : undefined;
}

/**
 * How a run-funnel event names its capsule, or null when it cannot name one.
 *
 * `isStoreCapsule` says how the item was CONSTRUCTED, not whether a capsule
 * exists: `featuredRunItems()` shadows a slug with a frontend-only entry, and
 * `community/hello-capsule` is BOTH a curated demo and a real Store capsule. So
 * a curated item still reports — but by REF only, never with its frontend id.
 *
 * Ingest resolves the ref: a real capsule (hello-capsule) lands on its actual
 * `capsules.id` and its runs are counted, and a genuinely frontend-only demo
 * (glow, which has no row) leaves `capsule_id` NULL and stays out of every
 * per-capsule metric. The client cannot tell those apart without a lookup —
 * and the App Page deliberately makes zero `/by-slug` calls for a curated
 * item — so the server, which already normalises every capsule name, decides.
 *
 * The trade-off is explicit: an unresolvable demo run still exists as a raw
 * event and counts toward PLATFORM-wide run totals. That is strictly better
 * than the alternative it replaces, which silently dropped a real capsule's
 * runs entirely.
 */
export function funnelCapsule(
  item: { id?: string; ref?: string; isStoreCapsule?: boolean } | null | undefined,
): { id?: string; ref: string } | null {
  if (!item?.ref) return null;
  return item.isStoreCapsule && item.id
    ? { id: item.id, ref: item.ref }
    : { ref: item.ref };
}

export function toRunFeedItem(s: CapsuleSummary): RunFeedItem {
  const surfaces = Array.isArray(s.surfaces) ? s.surfaces : [];
  return {
    id: s.id,
    slug: s.slug,
    ref: capsuleRef(s),
    isStoreCapsule: true,
    priceCents: typeof s.price === "number" ? s.price : 0,
    currency: typeof s.currency === "string" ? s.currency : "usd",
    name: s.name,
    shortDescription: (s.short_description || s.description || "").trim(),
    iconUrl: pickIcon(s),
    coverUrl: pickCover(s),
    ownerHandle: (s.publisher?.handle ?? "").trim(),
    ownerDisplay:
      (s.publisher?.display_name || s.publisher?.displayName || s.publisher?.handle || "").trim(),
    ownerAvatarUrl: resolvePublisherAvatarUrl(
      s.publisher
        ? {
            handle: s.publisher.handle,
            github_id: s.publisher.github_id ?? s.publisher.githubId ?? null,
            github_login: s.publisher.github_login ?? s.publisher.githubLogin ?? null,
            image: s.publisher.image ?? null,
            avatar_url: s.publisher.avatar_url ?? s.publisher.avatarUrl ?? null,
          }
        : null,
    ),
    sourceUrl: "",
    repository: (s.repository ?? "").trim() || undefined,
    postedAt: s.created_at ?? s.createdAt ?? null,
    surfaces,
    category: s.primary_category || s.primaryCategory || null,
    tags: Array.isArray(s.tags) ? s.tags : [],
    isFrontendOnly: isFrontendOnly(surfaces),
    runs: readRunsCount(s),
    vouches: readVouchesCount(s),
  };
}

export function toRunFeedItems(list: CapsuleSummary[] | undefined | null): RunFeedItem[] {
  return (Array.isArray(list) ? list : []).map(toRunFeedItem);
}

/**
 * Prepend `featured` ahead of `items`, deduped by `ref` (a featured/curated
 * item always wins over a same-ref item from `items`). The shared dedupe
 * primitive both {@link mergeShowcaseFeed} (capsule search) and the
 * showcase-feed client (src/api/feedClient.ts, PR-M — either its real
 * endpoint response or its own capsule-search fallback) merge through, so
 * there is exactly one "featured-first, deduped" rule regardless of source.
 */
export function mergeFeedItems(featured: RunFeedItem[], items: RunFeedItem[]): RunFeedItem[] {
  const seen = new Set(featured.map((i) => i.ref));
  const rest = items.filter((i) => i.ref && !seen.has(i.ref));
  return [...featured, ...rest];
}

/**
 * The Showcase feed: curated demos (with a live autoplay preview) pinned at the
 * front, then real Store capsules, deduped against the curated refs.
 */
export function mergeShowcaseFeed(
  featured: RunFeedItem[],
  capsules: CapsuleSummary[] | undefined | null,
): RunFeedItem[] {
  return mergeFeedItems(featured, toRunFeedItems(capsules));
}

export function formatRunsCount(runs: number | null): string | null {
  if (runs == null || !Number.isFinite(runs) || runs < 0) return null;
  if (getLocale() !== "en") return new Intl.NumberFormat(getLocale(), { notation: runs >= 1000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(runs);
  if (runs < 1000) return String(runs);
  if (runs < 1_000_000) return `${(runs / 1000).toFixed(runs < 10_000 ? 1 : 0)}K`;
  return `${(runs / 1_000_000).toFixed(runs < 10_000_000 ? 1 : 0)}M`;
}

/** "N run(s)" / "1.2K runs" — {@link formatRunsCount} plus the unit word,
 *  for contexts (card meta line, app detail header) that don't already
 *  label the number separately the way RunsActionRail's caption does. */
export function formatRunLabel(runs: number | null): string | null {
  const count = formatRunsCount(runs);
  if (count === null) return null;
  return plural(runs!, "{{value0}} run", "{{value0}} runs", { value0: count });
}

/** "N upvote(s)" / "1.2K upvotes" — same K/M abbreviation as
 *  {@link formatRunLabel}, for the vouch count (card meta line, app detail
 *  header). */
export function formatVouchLabel(vouches: number | null): string | null {
  const count = formatRunsCount(vouches);
  if (count === null) return null;
  return plural(vouches!, "{{value0}} upvote", "{{value0}} upvotes", { value0: count });
}

/** "N comment(s)" / "1.2K comments" — same K/M abbreviation as
 *  {@link formatRunLabel}, for the comment count (card meta line). */
export function formatCommentLabel(comments: number | null): string | null {
  const count = formatRunsCount(comments);
  if (count === null) return null;
  return plural(comments!, "{{value0}} comment", "{{value0}} comments", { value0: count });
}
